/**
 * "Gesundheit"-Tab im Mandanten-Kontrollzentrum (Admin-Bereich V2 P1,
 * docs/KONZEPT-ADMIN-BEREICH.md § "Mandanten-Detail, 8 Tabs" Punkt 6: "letzte `system_errors`,
 * Bounce-Liste, 'zuletzt aktiv' aus `activity_log`").
 *
 * PRAEZISIERUNG gegenueber dem Konzept-Wortlaut (docs/LOOP-ADMIN-BEREICH.md P1, fuenftes
 * Kriterium): es gibt KEINE Admin-Route, die einzelne `system_errors`-/`emails`-Zeilen auflistet
 * — nur `platform_admin_nutzung_uebersicht()` (Migration 373, ueber `GET /api/admin/nutzung`)
 * liefert AGGREGIERTE Zahlen je Mandant (`fehler7d`, `letzteAktivitaet`, `memberCount`, ...).
 * Dieser Tab zeigt deshalb Kennzahlen statt Listen — eine Einzel-Fehlerliste waere erfunden, ohne
 * dass die API sie hergibt (harte Vorgabe: "lieber ein kleinerer, ehrlicher Tab"). Eine
 * Detail-Liste ist eine spaetere Erweiterung (eigene Route noetig), siehe Bericht der Iteration.
 *
 * Datenquelle bewusst die PLATTFORMWEITE Route (`GET /api/admin/nutzung`, kein `:id`-Parameter,
 * liefert eine Zeile je Mandant) statt einer eigenen Filter-Route — die gibt es nicht, und bei
 * 3–50 Mandanten (Konzept § "Verworfen: Materialisierte Sicht") ist ein Live-Filter im Client
 * unproblematisch. `letzteAktivitaet`/`fehler7d` stehen NUR hier zur Verfuegung, nicht in der
 * Tages-Zeitreihe (`GET /api/admin/tenants/:id/nutzung`, siehe `NutzungTab.tsx`).
 */
import { useApi } from "../../../hooks/useApi";
import { EmptyState, ErrorNote, KpiCard, LoadingRows } from "../../../ui/components";
import { formatDateTime } from "../../../ui/format";

/** 1:1 aus `apps/api/src/routes/admin.ts` `NutzungUebersichtRow` uebernommen (JSON-Antwort:
 *  `letzteAktivitaet` kommt als ISO-String oder `null` an, nicht als `Date`). */
interface NutzungUebersichtRow {
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  memberCount: number;
  letzteAktivitaet: string | null;
  fehler7d: number;
  mails30d: number;
  bounce30d: number;
  bounceQuote30d: number | null;
  kiKosten30dCents: number;
}

export function GesundheitTab({ tenantId }: { tenantId: string }) {
  const { data, loading, error, reload } = useApi<NutzungUebersichtRow[]>("/api/admin/nutzung");
  const zeile = data?.find((r) => r.tenantId === tenantId) ?? null;

  if (loading) {
    return (
      <div className="card mt-4 overflow-hidden">
        <LoadingRows rows={3} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="card mt-4 overflow-hidden">
        <ErrorNote message={error} onRetry={reload} />
      </div>
    );
  }
  if (!zeile) {
    return (
      <div className="card mt-4 overflow-hidden">
        <EmptyState
          icon="favorite"
          title="Keine Nutzungsdaten"
          hint="Für diesen Mandanten liegen noch keine aggregierten Kennzahlen vor."
        />
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiCard
          label="Zuletzt aktiv"
          value={zeile.letzteAktivitaet ? formatDateTime(zeile.letzteAktivitaet) : "Noch nie aktiv"}
          icon="bolt"
          iconTone={zeile.letzteAktivitaet ? "primary" : "warn"}
        />
        <KpiCard
          label="Fehler (7 Tage)"
          value={zeile.fehler7d}
          icon="error"
          iconTone={zeile.fehler7d > 0 ? "warn" : "success"}
        />
        <KpiCard label="Mitglieder" value={zeile.memberCount} icon="group" />
      </div>
      <div className="card p-4">
        <p className="font-body-sm text-[12.5px] text-secondary-fixed-dim">
          Eine Liste einzelner Fehlermeldungen (<code>system_errors</code>) oder gebouncter E-Mails ist über
          die Admin-API aktuell nicht verfügbar — nur die oben gezeigten aggregierten Zahlen. Eine
          Detail-Liste wäre eine spätere Erweiterung.
        </p>
      </div>
    </div>
  );
}
